import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { preloadCriticalImages, preloadPageImages, preloadBackgroundImages, clearImageCache } from '../utils/imagePreloader';
import { getAllQueuedAssessments, clearOfflineAssessment } from '../utils/offlineAssessment';
import { toast } from 'react-toastify';
import { API_CONFIG } from '../config/api';

interface User {
  id: string;
  email: string;
  profile: {
    firstName?: string;
    lastName?: string;
    preferredName?: string;
    phoneNumber?: string;
    gender?: string;
    age?: number;
    education?: {
      status?: string;
      school?: string;
      yearLevel?: string;
      program?: string;
    };
    skills?: {
      primaryArea?: string;
      technicalSkills?: string[];
    };
    profilePicture?: string;
  };
  location?: {
    region?: {
      code?: string;
      name?: string;
      regionName?: string;
    };
    province?: {
      code?: string;
      name?: string;
    };
    city?: {
      code?: string;
      name?: string;
    };
    barangay?: {
      code?: string;
      name?: string;
    };
  };
  isEmailVerified: boolean;
  role: string;
  lastLogin?: string;
  lastActive?: string;
  createdAt: string;
  createdBy?: string;
}

interface TokenUsage {
  totalTokens: number;
  messagesCount: number;
  dailyTokens: number;
  dailyMessages: number;
  yearlyTokens: number;
  yearlyMessages: number;
  lastUpdated: string;
}

interface ContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  tokenUsage: TokenUsage;
  login: (token: string, refreshToken: string, user: User) => void;
  logout: () => void;
  updateUser: (user: User) => void;
  updateTokenUsage: (tokensUsed: number) => void;
  fetchTokenUsage: () => Promise<void>;
}

const AuthContext = createContext<ContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tokenUsage, setTokenUsage] = useState<TokenUsage>({
    totalTokens: 0,
    messagesCount: 0,
    dailyTokens: 0,
    dailyMessages: 0,
    yearlyTokens: 0,
    yearlyMessages: 0,
    lastUpdated: new Date().toISOString()
  });

  useEffect(() => {
    // Check for stored authentication data on app load
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    // Safety timeout to prevent infinite loading (30 seconds max)
    const safetyTimeout = setTimeout(() => {

      setIsLoading(false);
    }, 30000);

    if (storedToken && storedUser) {
      // Validate token before setting as authenticated
      const validateStoredToken = async () => {
        try {
          const userData = JSON.parse(storedUser);

          // Test token validity by making a simple API call with timeout
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/health`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${storedToken}`,
              'Content-Type': 'application/json'
            },
            signal: controller.signal
          });

          clearTimeout(timeoutId);

          if (response.ok) {
            // Token is valid, set as authenticated
            setToken(storedToken);
            setUser(userData);

            // Load token usage data
            const tokenUsageKey = `lifepsensor_token_usage_${userData.id}`;
            const storedTokenUsage = localStorage.getItem(tokenUsageKey);
            if (storedTokenUsage) {
              setTokenUsage(JSON.parse(storedTokenUsage));
            }

            // Fetch latest token usage from database
            fetchTokenUsage().catch((error) => {

            });

            // Preload critical images if user is already authenticated
            preloadCriticalImages().catch((error) => {

            });

            // Preload background images in the background
            preloadBackgroundImages();
          } else {
            // Token is invalid, clear stored data
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');

            // Clear token usage data
            const tokenUsageKey = `lifepsensor_token_usage_${userData.id}`;
            localStorage.removeItem(tokenUsageKey);
          }
        } catch (error) {
          // Check if this is a network connectivity issue
          const isNetworkError = error instanceof TypeError && error.message.includes('Failed to fetch');

          if (error instanceof Error && error.name === 'AbortError') {

          } else if (isNetworkError) {

            // For network errors, don't clear tokens - they might still be valid
            // Just set the user data from localStorage and continue
            try {
              const userData = JSON.parse(storedUser);
              setUser(userData);
              setToken(storedToken);
            } catch (parseError) {
              // If we can't parse stored user data, clear everything

              localStorage.removeItem('token');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('user');
              setToken(null);
              setUser(null);
            }
          } else {

            // Clear invalid data for actual authentication errors
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
          }
        } finally {
          // Always set loading to false when validation completes (success or failure)
          clearTimeout(safetyTimeout);
          setIsLoading(false);
        }
      };

      validateStoredToken();
    } else {
      // No stored authentication data, set loading to false immediately
      clearTimeout(safetyTimeout);
      setIsLoading(false);
    }
  }, []);

  const login = (newToken: string, refreshToken: string, userData: User) => {
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('token', newToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(userData));

    // Initialize token usage for new user
    const newTokenUsage: TokenUsage = {
      totalTokens: 0,
      messagesCount: 0,
      dailyTokens: 0,
      dailyMessages: 0,
      yearlyTokens: 0,
      yearlyMessages: 0,
      lastUpdated: new Date().toISOString()
    };
    setTokenUsage(newTokenUsage);
    const tokenUsageKey = `lifepsensor_token_usage_${userData.id}`;
    localStorage.setItem(tokenUsageKey, JSON.stringify(newTokenUsage));

    // Defer all non-critical operations to after login is complete
    // Use requestIdleCallback or setTimeout for better performance
    const deferOperations = () => {
      // Sync offline assessment data if any exists
      const queuedAssessments = getAllQueuedAssessments();
      const assessmentTypes = Object.keys(queuedAssessments);

      if (assessmentTypes.length > 0) {
        // Sync each assessment type
        assessmentTypes.forEach(assessmentType => {
          const offlineData = queuedAssessments[assessmentType];

          // Determine the correct endpoint based on assessment type
          let endpoint = API_CONFIG.ENDPOINTS.STAR_OF_WELLBEING;
          if (assessmentType === 'dass1') {
            // DASS-21 uses the mental health endpoint
            endpoint = API_CONFIG.ENDPOINTS.MENTAL_HEALTH;
          }

          // Use the properly formatted responses from assessmentData, not the raw responses
          const formattedResponses = offlineData.assessmentData?.responses || offlineData.responses;
          const payload = { responses: formattedResponses, duration: offlineData.duration || 0 };

          fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${newToken}`,
            },
            credentials: 'include',
            body: JSON.stringify(payload)
          })
          .then(response => {
            if (response.ok) {
              clearOfflineAssessment(assessmentType);

              const typeDisplayName = assessmentType === 'dass-star' ? 'DASS Star Assessment' :
                                     assessmentType === 'dass1' ? 'DASS-21 Assessment' : 'Assessment';

              toast.success(`Your saved ${typeDisplayName} has been synced to your account!`, {
                position: "top-center",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
              });
            } else {

              response.text().then(text => console.error('Response body:', text)).catch(() => {});

              // Silently fail - no toast notification shown
              // const typeDisplayName = assessmentType === 'dass-star' ? 'DASS Star Assessment' :
              //                        assessmentType === 'dass1' ? 'DASS-21 Assessment' : 'Assessment';

              // toast.error(`Failed to sync your saved ${typeDisplayName}. It will be retried next time you log in.`, {
              //   position: "top-center",
              //   autoClose: 5000,
              //   hideProgressBar: false,
              //   closeOnClick: true,
              //   pauseOnHover: true,
              //   draggable: true,
              // });
            }
          })
          .catch(error => {

            // Suppressed toast error notification below

            // const typeDisplayName = assessmentType === 'dass-star' ? 'DASS Star Assessment' :
            //                        assessmentType === 'dass1' ? 'DASS-21 Assessment' : 'Assessment';

            // toast.error(`Failed to sync your saved ${typeDisplayName}. It will be retried next time you log in.`, {
            //   position: "top-center",
            //   autoClose: 5000,
            //   hideProgressBar: false,
            //   pauseOnHover: true,
            //   draggable: true,
            // });
          });
        });
      }

      // Fetch token usage from database (non-blocking)
      fetchTokenUsage().catch((error) => {

      });

      // Preload critical images (logo, user avatar, etc.) - non-blocking
      preloadCriticalImages().catch((error) => {

      });

      // Preload dashboard images (most likely next page) - non-blocking
      preloadPageImages('dashboard').catch((error) => {

      });

      // Preload background images - non-blocking
      preloadBackgroundImages();
    };

    // Use requestIdleCallback if available, otherwise setTimeout with longer delay
    if ('requestIdleCallback' in window) {
      requestIdleCallback(deferOperations, { timeout: 2000 });
    } else {
      setTimeout(deferOperations, 2000); // Increased delay to 2 seconds
    }
  };

  const fetchTokenUsage = async () => {
    if (!user || !token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/token-usage/summary`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setTokenUsage(data.data);
          // Save to localStorage as backup
          const storageKey = `lifepsensor_token_usage_${user.id}`;
          localStorage.setItem(storageKey, JSON.stringify(data.data));
        }
      } else if (response.status === 401) {
        // Token is invalid, logout user
        logout();
        return;
      }
    } catch (error) {

      // Fallback to localStorage
      const storageKey = `lifepsensor_token_usage_${user.id}`;
      const cached = localStorage.getItem(storageKey);
      if (cached) {
        try {
          setTokenUsage(JSON.parse(cached));
        } catch (e) {

        }
      }
    }
  };

  const updateTokenUsage = async (tokensUsed: number) => {
    if (!user || !token) return;

    try {
      // Update in database
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/token-usage/update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ tokensUsed })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setTokenUsage(prev => ({
            ...prev,
            totalTokens: data.data.totalTokens,
            messagesCount: data.data.messagesCount,
            lastUpdated: data.data.lastUpdated
          }));
          // Refresh full usage data
          await fetchTokenUsage();
        }
      } else if (response.status === 401) {
        // Token is invalid, logout user
        logout();
        return;
      }
    } catch (error) {

      // Fallback to local update
      setTokenUsage(prev => {
        const updated = {
          ...prev,
          totalTokens: prev.totalTokens + tokensUsed,
          messagesCount: prev.messagesCount + 1,
          lastUpdated: new Date().toISOString()
        };

        // Save to localStorage
        const userId = user.id;
        const storageKey = `lifepsensor_token_usage_${userId}`;
        localStorage.setItem(storageKey, JSON.stringify(updated));

        return updated;
      });
    }
  };

  const logout = () => {
    // Clear token usage before clearing user data
    if (user) {
      const tokenUsageKey = `lifepsensor_token_usage_${user.id}`;
      localStorage.removeItem(tokenUsageKey);
    }
    
    setToken(null);
    setUser(null);
    setTokenUsage({
      totalTokens: 0,
      messagesCount: 0,
      dailyTokens: 0,
      dailyMessages: 0,
      yearlyTokens: 0,
      yearlyMessages: 0,
      lastUpdated: new Date().toISOString()
    });
    
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');

    // Clear Physical Learn assessment local data
    localStorage.removeItem('physicalWellnessLearnAssessment');

    // Explicitly clear chat cache for current user (belt and suspenders)
    if (user?.id) {
      localStorage.removeItem(`lifepsensor_chat_cache_${user.id}`);
    }
    
    // Clear all user-specific caches
    // Remove old cache keys (for backwards compatibility)
    localStorage.removeItem('lifepsensor_chat_cache');
    localStorage.removeItem('lifepsensor_health_assessments_cache');
    localStorage.removeItem('lifepsensor_insights_cache');
    
    // Remove all user-specific cache keys (format: lifepsensor_*_cache_userId)
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('lifepsensor_') && key.includes('_cache_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Optionally remove any custom physical-related cache keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.toLowerCase().includes('physical')) {
        localStorage.removeItem(key);
      }
    }
    
    clearImageCache(); // Clear image preload cache
  };

  const updateUser = (userData: User) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const isAuthenticated = !!token && !!user;

  const value: ContextType = {
    user,
    token,
    isAuthenticated,
    isLoading,
    tokenUsage,
    login,
    logout,
    updateUser,
    updateTokenUsage,
    fetchTokenUsage
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

